import { core, parseDay } from '@map-my-money/engine';
import {
  biggestMoves, CHECK_IN_LABELS, checkInFlowRows, checkInPrefill, checkInWindow, durationText, encodeChanges, expectedFlows, MAX_CHANGES_PARAM, untouchedFields,
  type CheckInBalanceChange, type CheckInCause, type CheckInFlowsBody, type CheckInResult, type MapChange, type Move,
} from '@map-my-money/views';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { CheckInBody } from '../api/types.ts';
import { t, type MessageKey } from '../i18n/index.ts';
import { todayString } from '../lib/clock.ts';
import { dayMonth, daysSpan, longDate, money, monthName, monthYear } from '../lib/format.ts';
import { decimalString, decode, waypointBalances } from '../lib/model.ts';
import { useStore, type CheckInRefusal } from '../state/store.tsx';
import { AmountInput, groupedAmount, parseAmount } from './AmountInput.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { changeCount, isLongGap, WhatHappened } from './WhatHappened.tsx';

type Field = 'cash' | 'investments' | `debt:${string}`;
type StepId = 'happened' | 'balances' | 'done';

interface Step {
  readonly id: StepId;
  readonly label: MessageKey;
  readonly short: MessageKey;
}

const HAPPENED: Step = { id: 'happened', label: 'waypoint.step.happened', short: 'waypoint.step.happened.short' };
const BALANCES: Step = { id: 'balances', label: 'waypoint.step.balances', short: 'waypoint.step.balances.short' };
const DONE: Step = { id: 'done', label: 'waypoint.step.done', short: 'waypoint.step.done' };
const WITH_STEP_ONE: readonly Step[] = [HAPPENED, BALANCES, DONE];
/** When there is nothing to review (the map was set up today, or the last check-in was today). */
const WITHOUT_STEP_ONE: readonly Step[] = [BALANCES, DONE];

const prefilled = (amount: string) => groupedAmount(String(Math.round(Number(amount))));

/** The iPhone's threshold: a gap worth asking about is at least £100 and 10% of what was expected. */
const significant = (gap: number, expected: number) => Math.abs(gap) >= 100 && (expected === 0 || Math.abs(gap) / Math.abs(expected) >= 0.1);

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const FIELD_OF_PATH = (path: string): Field | 'date' | null => {
  if (path === '/date') return 'date';
  if (path === '/balances/cash') return 'cash';
  if (path === '/balances/investments') return 'investments';
  const debt = /^\/debts\/(.+)$/.exec(path);
  return debt ? `debt:${debt[1]}` : null;
};

const joinNames = (names: readonly string[]) =>
  names.length <= 1 ? (names[0] ?? '') : t('waypoint.estimate.join', { first: names.slice(0, -1).join(', '), last: names[names.length - 1]! });

/** What we tell someone whose check-in kept pre-filled numbers as estimates (decision 2), once. */
export function estimateMessage(estimates: readonly Field[], all: readonly Field[], nameOf: (f: Field) => string): string | null {
  if (estimates.length === 0) return null;
  if (estimates.length === all.length) return t('waypoint.estimate.all');
  const first = estimates[0]!;
  const hint = first === 'cash' ? t('waypoint.estimate.hint.cash') : first === 'investments' ? t('waypoint.estimate.hint.investments') : t('waypoint.estimate.hint.debt');
  const kept = t(estimates.length === 1 ? 'waypoint.estimate.one' : 'waypoint.estimate.many', { fields: joinNames(estimates.map(nameOf)) });
  return `${kept} ${hint}`;
}

/** Keeps the sheet (and its pinned button) inside what the phone shows above the keyboard. */
function useVisualViewport(panel: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const fit = () => {
      const el = panel.current;
      if (!el) return;
      const covered = window.innerHeight - vv.height - vv.offsetTop;
      el.style.setProperty('--keyboard', `${Math.max(0, covered)}px`);
    };
    fit();
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    return () => {
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
    };
  }, [panel]);
}

/**
 * The check-in's dialog: a step bar, one step at a time, focus on each step's heading and back on the
 * opener when it closes. Ctrl/Cmd+Enter runs the main button.
 */
function StepShell({ steps, current, headingId, onClose, onPrimary, children, footer, announcement }: {
  steps: readonly Step[];
  current: StepId;
  headingId: string;
  onClose: () => void;
  onPrimary: () => void;
  children: ReactNode;
  footer: ReactNode;
  /** What a change inside the step says to a screen reader. */
  announcement?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const primary = useRef(onPrimary);
  primary.current = onPrimary;
  const close = useRef(onClose);
  close.current = onClose;
  const index = steps.findIndex((s) => s.id === current);
  const [announced, setAnnounced] = useState('');
  useVisualViewport(panel);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    document.body.classList.add('has-modal');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
      else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        primary.current();
      } else if (e.key === 'Tab' && panel.current) {
        const focusables = [...panel.current.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), select, textarea')].filter((el) => el.tabIndex >= 0);
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && (document.activeElement === first || !panel.current.contains(document.activeElement))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('has-modal');
      opener?.focus?.();
    };
  }, []);

  useEffect(() => {
    document.getElementById(headingId)?.focus({ preventScroll: true });
    panel.current?.querySelector('.checkin__body')?.scrollTo({ top: 0 });
    setAnnounced(t('waypoint.step.announce', { n: index + 1, total: steps.length, name: t(steps[index]!.label) }));
  }, [current, headingId, index, steps]);

  return (
    <div className="modal checkin">
      <div className="modal__scrim" onClick={onClose} aria-hidden="true" />
      <div ref={panel} className="modal__panel checkin__panel" role="dialog" aria-modal="true" aria-labelledby={headingId} data-step={current}>
        <div className="checkin__head">
          <ol className="checkin__steps" aria-label={t('waypoint.steps')}>
            {steps.map((s, i) => (
              <li key={s.id} className={`checkin__step ${i < index ? 'checkin__step--done' : ''} ${i === index ? 'checkin__step--current' : ''}`} {...(i === index ? { 'aria-current': 'step' as const } : {})}>
                <span className="checkin__step-dot" aria-hidden="true">{i < index ? <Icon name="check" size={12} strokeWidth={3} /> : i + 1}</span>
                <span className="checkin__step-label">{t(s.label)}</span>
                <span className="checkin__step-label checkin__step-label--short" aria-hidden="true">{t(s.short)}</span>
              </li>
            ))}
          </ol>
          <button type="button" className="icon-button modal__close" aria-label={t('common.close')} onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </div>
        <p className="sr-only" aria-live="polite">{announced}</p>
        <p className="sr-only" aria-live="polite" data-testid="check-in-announce">{announcement}</p>
        <div className="checkin__body" key={current} tabIndex={0} role="group" aria-labelledby={headingId}>{children}</div>
        <div className="checkin__footer">{footer}</div>
      </div>
    </div>
  );
}

interface BalanceCard {
  readonly field: Field;
  readonly title: string;
  readonly inputLabel: string;
  readonly hint: string;
  readonly icon: IconName;
  readonly tone: 'mint' | 'violet' | 'terracotta';
  readonly isDebt: boolean;
}

/**
 * The check-in (R25, R27): first what happened since the last one (the payments the map expected, any
 * of them changed, skipped or added), then cash, investments and every debt still owing, each
 * pre-filled with what the map expects on the day plus what step 1 said. Typing or tapping Matches
 * checks a number; one left untouched is sent as an estimate, and an empty one as missing, so the
 * server refuses it rather than storing £0.
 */
export function CheckInDialog({ onClose }: { onClose: () => void }) {
  const { plan, waypoints, checkIn } = useStore();
  const id = useId();
  const headingId = `${id}-heading`;
  const today = todayString();
  const [date, setDate] = useState(today);
  const day = date > today ? today : date;
  const review = useMemo(() => (plan ? checkInWindow(plan, waypoints, day) : null), [plan, waypoints, day]);
  const reviewable = review !== null && !review.empty;
  const [step, setStep] = useState<StepId>(reviewable ? 'happened' : 'balances');
  const [changingDate, setChangingDate] = useState(false);
  const [flows, setFlows] = useState<CheckInFlowsBody>({});
  /** null until step 1 is done; false when skipped ("I don't remember"): never sent as confirmed. */
  const [reviewed, setReviewed] = useState<boolean | null>(null);
  const [typed, setTyped] = useState<ReadonlyMap<Field, string>>(new Map());
  const [matched, setMatched] = useState<ReadonlySet<Field>>(new Set());
  const [refused, setRefused] = useState<readonly CheckInRefusal[]>([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<{ result: CheckInResult | null; estimates: Field[]; changes: readonly MapChange[] } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const inputs = useRef(new Map<string, HTMLInputElement | null>());

  const steps = reviewable ? WITH_STEP_ONE : WITHOUT_STEP_ONE;
  const expectedPayments = useMemo(() => (plan && reviewable ? expectedFlows(plan, waypoints, review.from, review.to, today) : []), [plan, waypoints, reviewable, review, today]);
  const counted = reviewable && reviewed !== false;
  const rows = useMemo(() => (counted ? checkInFlowRows(expectedPayments, flows) : undefined), [counted, expectedPayments, flows]);
  const mapPrefill = useMemo(() => (plan ? checkInPrefill(plan, waypoints, day, today) : null), [plan, waypoints, day, today]);
  const prefill = useMemo(() => (plan && rows ? checkInPrefill(plan, waypoints, day, today, rows) : mapPrefill), [plan, waypoints, day, today, rows, mapPrefill]);
  const changes = changeCount(flows);
  const first = !waypoints.some((w) => w.date < day);

  const cards: BalanceCard[] = useMemo(() => [
    { field: 'cash', title: CHECK_IN_LABELS.cash, inputLabel: CHECK_IN_LABELS.cash, hint: t('waypoint.cashHint'), icon: 'wallet', tone: 'mint', isDebt: false },
    { field: 'investments', title: CHECK_IN_LABELS.investments, inputLabel: CHECK_IN_LABELS.investments, hint: t('waypoint.investmentsHint'), icon: 'trend', tone: 'violet', isDebt: false },
    ...(prefill?.debts ?? []).map((d): BalanceCard => ({
      field: `debt:${d.id}`, title: d.label, inputLabel: `${CHECK_IN_LABELS.debt} ${d.label}`, hint: '', icon: d.mortgage ? 'home' : 'card', tone: 'terracotta', isDebt: true,
    })),
  ], [prefill]);
  const fields = cards.map((c) => c.field);
  const expectedOf = (p: typeof prefill) => {
    const map = new Map<Field, string>();
    if (!p) return map;
    map.set('cash', p.cash);
    map.set('investments', p.investments);
    for (const d of p.debts) map.set(`debt:${d.id}`, d.balance);
    return map;
  };
  const expected = useMemo(() => expectedOf(prefill), [prefill]);
  const mapExpected = useMemo(() => expectedOf(mapPrefill), [mapPrefill]);

  const valueOf = (f: Field) => typed.get(f) ?? prefilled(expected.get(f) ?? '');
  const amountIn = (entries: ReadonlyMap<Field, string>, f: Field) => {
    const text = entries.get(f);
    return text === undefined ? (expected.has(f) ? Number(expected.get(f)) : null) : parseAmount(text);
  };
  const amountOf = (f: Field) => amountIn(typed, f);
  const touched = new Set<string>([...typed.keys(), ...matched]);
  const malformedIn = (entries: ReadonlyMap<Field, string>, f: Field) => {
    const n = amountIn(entries, f);
    return Number.isNaN(n) || (f !== 'cash' && n !== null && n < 0);
  };
  const malformed = (f: Field) => malformedIn(typed, f);
  const valid = fields.every((f) => !malformed(f));
  const withFlows = fields.some((f) => Math.round(Number(expected.get(f) ?? '0') * 100) !== Math.round(Number(mapExpected.get(f) ?? expected.get(f) ?? '0') * 100));
  const valuedFromMap = (plan?.assets?.length ?? 0) + (plan?.properties?.length ?? 0) + (plan?.depreciatingAssets?.length ?? 0) > 0;
  const checkedCount = fields.filter((f) => touched.has(f)).length;
  const refusalOf = (f: Field | 'date') => refused.find((r) => FIELD_OF_PATH(r.path) === f)?.message ?? null;
  const unplaced = refused.filter((r) => FIELD_OF_PATH(r.path) === null || (FIELD_OF_PATH(r.path) !== 'date' && !fields.includes(FIELD_OF_PATH(r.path) as Field)));
  const nameOf = (f: Field) => (f === 'cash' ? t('waypoint.field.cash') : f === 'investments' ? t('waypoint.field.investments') : cards.find((c) => c.field === f)?.title ?? f);

  const monthsSinceLast = useMemo(() => {
    const last = review?.previous ?? plan?.configuration.initialBalanceDate ?? null;
    return last ? Math.max(1, Math.round((parseDay(day) - parseDay(last)) / 30.44)) : 1;
  }, [review, plan, day]);

  const edit = (f: Field, text: string) => {
    setTyped((m) => new Map(m).set(f, text));
    setMatched((s) => {
      const next = new Set(s);
      next.delete(f);
      return next;
    });
    setRefused((r) => r.filter((x) => FIELD_OF_PATH(x.path) !== f));
  };
  const match = (f: Field) => {
    setTyped((m) => {
      const next = new Map(m);
      next.delete(f);
      return next;
    });
    setMatched((s) => new Set(s).add(f));
    setRefused((r) => r.filter((x) => FIELD_OF_PATH(x.path) !== f));
  };
  const changeDate = (next: string) => {
    setDate(next || today);
    setTyped(new Map());
    setMatched(new Set());
    setRefused([]);
    setFlows({});
    setReviewed(null);
  };

  const focusFirstRefusal = (issues: readonly CheckInRefusal[]) => {
    const order: (Field | 'date')[] = ['date', ...fields];
    const first = order.find((f) => issues.some((r) => FIELD_OF_PATH(r.path) === f));
    if (first === 'date') setChangingDate(true);
    requestAnimationFrame(() => inputs.current.get(first ?? '')?.focus());
  };

  const finishStepOne = (asReviewed: boolean) => {
    setReviewed(asReviewed);
    setStep('balances');
  };

  /** With `allMatch`, every balance is confirmed as pre-filled (one tap for a month that went as expected). */
  const submit = async (allMatch = false) => {
    const entries = allMatch ? new Map<Field, string>() : typed;
    if (!plan || !prefill || busy || fields.some((f) => malformedIn(entries, f))) return;
    if (allMatch) {
      setTyped(entries);
      setMatched(new Set(fields));
      setRefused([]);
    }
    setBusy(true);
    const amount = (f: Field) => amountIn(entries, f);
    const cash = amount('cash');
    const invested = amount('investments');
    const debts = Object.fromEntries(prefill.debts.flatMap((d) => {
      const owed = amount(`debt:${d.id}`);
      return owed === null ? [] : [[d.id, decimalString(core.fromDouble(owed))]];
    }));
    const balances = cash !== null && invested !== null
      ? waypointBalances(decode(plan), core.fromDouble(cash), core.fromDouble(invested), parseDay(date), parseDay(today), debts)
      : {
          ...(cash === null ? {} : { cash: decimalString(core.fromDouble(cash)) }),
          ...(invested === null ? {} : { investments: decimalString(core.fromDouble(invested)) }),
        };
    const estimates = allMatch ? [] : untouchedFields(prefill, touched).filter((f) => amount(f) !== null);
    const body = {
      date, balances,
      ...(prefill.debts.length > 0 ? { debts } : {}),
      ...(estimates.length > 0 ? { estimates } : {}),
      ...(counted && reviewed === true ? { flows: { from: review!.from, ...flows } } : {}),
    } as CheckInBody;
    const outcome = await checkIn(body);
    setBusy(false);
    if (!outcome.saved) {
      setRefused(outcome.issues);
      if (outcome.issues.some((r) => r.path.startsWith('/flows'))) setStep('happened');
      else focusFirstRefusal(outcome.issues);
      return;
    }
    setSaved({ result: outcome.result, estimates, changes: outcome.changes ?? [] });
    setStep('done');
  };

  const isToday = date === today;
  const shortcut = isMac() ? '⌘ Enter' : 'Ctrl Enter';

  if (step === 'done' && saved) {
    return (
      <StepShell
        steps={steps} current="done" headingId={headingId} onClose={onClose} onPrimary={onClose}
        footer={
          <div className="checkin__actions checkin__actions--done">
            <Link to="/forecast" className="button button--ghost checkin__secondary" onClick={onClose}>{t('waypoint.done.forecast')}</Link>
            <button type="button" className="button button--primary checkin__primary" onClick={onClose}>{t('common.done')}</button>
          </div>
        }
      >
        <DoneStep headingId={headingId} result={saved.result} date={date} changes={saved.changes} estimateNote={estimateMessage(saved.estimates, fields, nameOf)} onClose={onClose} />
      </StepShell>
    );
  }

  if (step === 'happened' && plan && review && reviewable) {
    const flowRefusals = refused.filter((r) => r.path.startsWith('/flows'));
    const cash = prefill?.cash ?? '0';
    const longGap = !first && review.previous !== null && isLongGap(review.previous, day);
    /** After a long gap, going straight to the balances leads, until something in step 1 is changed. */
    const skipLeads = longGap && changes === 0;
    const role = (leads: boolean) => (leads
      ? { className: 'button button--primary checkin__primary', 'aria-keyshortcuts': 'Control+Enter Meta+Enter' }
      : { className: 'button button--ghost checkin__secondary' });
    const skip = (
      <button key="skip" type="button" {...role(skipLeads)} onClick={() => finishStepOne(false)} data-testid="flows-skip" aria-label={t(longGap ? 'flows.skip.long' : 'flows.skip')}>
        <span className="flows__skip-long" aria-hidden="true">{t(longGap ? 'flows.skip.long' : 'flows.skip')}</span>
        <span className="flows__skip-short" aria-hidden="true">{t(longGap ? 'flows.skip.long.short' : 'flows.skip.short')}</span>
      </button>
    );
    const next = (
      <button key="next" type="button" {...role(!skipLeads)} onClick={() => finishStepOne(true)} data-testid="flows-next">
        {changes === 0 ? t('flows.next.all') : t('flows.next')}
        {changes > 0 && <Icon name="arrowRight" size={16} />}
      </button>
    );
    return (
      <StepShell
        steps={steps} current="happened" headingId={headingId} onClose={onClose} onPrimary={() => finishStepOne(!skipLeads)} announcement={announcement}
        footer={
          <>
            <p className="checkin__checked flows__summary" data-testid="flows-summary" aria-live="polite">
              {changes === 0 ? t('flows.changes.none') : t(changes === 1 ? 'flows.changes.short.one' : 'flows.changes.short', { n: changes, amount: money(Math.round(Number(cash))) })}
            </p>
            <div className="checkin__actions">
              <span className="checkin__shortcut" aria-hidden="true">{t('flows.shortcut', { keys: shortcut })}</span>
              {skipLeads ? [next, skip] : [skip, next]}
            </div>
          </>
        }
      >
        <WhatHappened
          headingId={headingId} plan={plan} date={day} window={review} expected={expectedPayments} body={flows}
          onChange={(next) => { setFlows(next); setRefused((r) => r.filter((x) => !x.path.startsWith('/flows'))); }}
          first={first} monthsSince={monthsSinceLast} longGap={longGap} cash={cash} mapCash={mapPrefill?.cash ?? cash} announce={setAnnouncement}
        />
        {flowRefusals.length > 0 && (
          <div className="checkin__refused" role="alert" data-testid="check-in-refused">
            <p className="checkin__refused-title">{t('waypoint.refused')}</p>
            <ul>{flowRefusals.map((r) => <li key={r.message}>{r.message}</li>)}</ul>
          </div>
        )}
      </StepShell>
    );
  }

  return (
    <StepShell
      steps={steps} current="balances" headingId={headingId} onClose={onClose} onPrimary={() => void submit()}
      footer={
        <>
          <p className={`checkin__checked ${checkedCount === fields.length ? 'checkin__checked--all' : ''}`} data-testid="check-in-checked" aria-live="polite">
            {checkedCount === fields.length && <Icon name="check" size={13} strokeWidth={3} />}
            {checkedCount === fields.length
              ? t('waypoint.checked.all', { n: fields.length })
              : fields.length - checkedCount === 1 ? t('waypoint.checked.left.one') : t('waypoint.checked.left', { n: fields.length - checkedCount })}
          </p>
          <div className="checkin__actions">
            <span className="checkin__shortcut" aria-hidden="true">{t('waypoint.shortcut', { keys: shortcut })}</span>
            {reviewable && (
              <button type="button" className="button button--ghost checkin__secondary checkin__back" onClick={() => setStep('happened')} data-testid="check-in-back">
                <Icon name="chevronLeft" size={16} />{t('waypoint.back')}
              </button>
            )}
            <button type="button" className="button button--primary checkin__primary" disabled={!valid || busy} onClick={() => void submit()} aria-keyshortcuts="Control+Enter Meta+Enter">
              {t('waypoint.save')}
            </button>
          </div>
        </>
      }
    >
      <div className="checkin__intro">
        <h2 id={headingId} className="checkin__title" tabIndex={-1}>
          {!reviewable && first ? t('waypoint.heading.first') : isToday ? t('waypoint.heading.today') : t('waypoint.heading.on', { date: dayMonth(parseDay(date)) })}
        </h2>
        <p className="checkin__date">
          <span>{longDate(parseDay(date))}</span>
          {!changingDate && (
            <button type="button" className="link-button" onClick={() => { setChangingDate(true); requestAnimationFrame(() => inputs.current.get('date')?.focus()); }}>
              {t('waypoint.date.change')}
            </button>
          )}
        </p>
        {changingDate && (
          <div className={`checkin__date-field ${refusalOf('date') ? 'checkin__date-field--invalid' : ''}`}>
            <label htmlFor={`${id}-date`} className="field__label">{t('waypoint.date')}</label>
            <div className="checkin__date-row">
              <input
                ref={(el) => { inputs.current.set('date', el); }}
                id={`${id}-date`} className="input" type="date" max={today} value={date}
                aria-describedby={`${id}-date-hint`} aria-invalid={refusalOf('date') ? true : undefined}
                onChange={(e) => changeDate(e.target.value)}
              />
              {!isToday && <button type="button" className="button button--ghost" onClick={() => changeDate(today)}>{t('waypoint.date.today')}</button>}
            </div>
            <span id={`${id}-date-hint`} className={refusalOf('date') ? 'checkin__refusal' : 'field__hint'}>{refusalOf('date') ?? t('waypoint.dateHint')}</span>
          </div>
        )}
        <p className="checkin__lead">{t(withFlows ? 'waypoint.intro.withFlows' : 'waypoint.intro')}</p>
      </div>
      {typed.size === 0 && (
        <button
          type="button" className="checkin-all-match" disabled={busy} onClick={() => void submit(true)}
          aria-label={t('waypoint.allMatch.label')} data-testid="check-in-all-match"
        >
          <span className="checkin-all-match__tick" aria-hidden="true"><Icon name="check" size={16} strokeWidth={3} /></span>
          {t('waypoint.allMatch')}
        </button>
      )}

      <div className="checkin__cards">
        {cards.filter((c) => !c.isDebt).map((c) => balanceCard(c))}
      </div>
      {cards.some((c) => c.isDebt) && (
        <section className="checkin__debts" aria-labelledby={`${id}-debts`}>
          <div className="checkin__group-head">
            <h3 id={`${id}-debts`} className="checkin__group-title">{t('waypoint.debts')}</h3>
            <span className="checkin__group-hint">{t('waypoint.debtsHint')}</span>
          </div>
          <div className="checkin__cards">{cards.filter((c) => c.isDebt).map((c) => balanceCard(c))}</div>
        </section>
      )}
      {valuedFromMap && <p className="checkin__note">{t('waypoint.debtNote')}</p>}
      {unplaced.length > 0 && (
        <div className="checkin__refused" role="alert" data-testid="check-in-refused">
          <p className="checkin__refused-title">{t('waypoint.refused')}</p>
          <ul>{unplaced.map((r) => <li key={r.message}>{r.message}</li>)}</ul>
        </div>
      )}
    </StepShell>
  );

  function balanceCard(c: BalanceCard) {
    const want = Number(expected.get(c.field) ?? '0');
    const amount = amountOf(c.field);
    const isTouched = touched.has(c.field);
    const isMatched = matched.has(c.field);
    const gap = amount === null || Number.isNaN(amount) ? 0 : amount - want;
    const better = c.isDebt ? gap < 0 : gap > 0;
    const refusal = refusalOf(c.field);
    const error = malformed(c.field) ? t('plan.field.number') : refusal;
    const inputId = `${id}-${c.field}`;
    const suggestion = !isTouched || c.field === 'investments' || amount === null || !significant(gap, want)
      ? null
      : c.field === 'cash'
        ? (gap < 0 ? 'cost' : 'income')
        : c.isDebt && gap > 0 ? 'cost' : null;
    const perMonth = Math.round(Math.abs(gap) / monthsSinceLast);
    const status = !isTouched ? null : Math.round(gap) === 0 ? t('waypoint.diff.same') : t(gap < 0 ? 'waypoint.diff.less' : 'waypoint.diff.more', { amount: money(Math.abs(gap)) });
    return (
      <div key={c.field} className={`checkin-card ${isTouched ? 'checkin-card--checked' : ''} ${error ? 'checkin-card--refused' : ''}`} data-testid={`check-in-card-${c.field}`}>
        <div className="checkin-card__top">
          <span className={`checkin-card__icon checkin-card__icon--${c.tone}`} aria-hidden="true"><Icon name={c.icon} size={16} /></span>
          <span className="checkin-card__text">
            <span className="checkin-card__title">{c.title}</span>
            {c.hint && <span className="checkin-card__hint">{c.hint}</span>}
          </span>
          {status && (
            <span className={`checkin-card__diff ${Math.round(gap) === 0 ? '' : better ? 'checkin-card__diff--better' : 'checkin-card__diff--worse'}`} data-testid="check-in-diff">{status}</span>
          )}
        </div>
        <div className="checkin-card__entry">
          <AmountInput
            block="checkin-amount" className={`${!isTouched ? 'checkin-amount--estimate' : ''} ${error ? 'checkin-amount--invalid' : ''}`.trim() || undefined}
            id={inputId} value={valueOf(c.field)} onChange={(text) => edit(c.field, text)} aria-label={c.inputLabel}
            aria-describedby={`${inputId}-expected ${inputId}-error`} aria-invalid={error !== null || undefined}
            inputRef={(el) => { inputs.current.set(c.field, el); }}
          />
          <button
            type="button" className={`checkin-match ${isMatched ? 'checkin-match--on' : ''}`} aria-pressed={isMatched}
            aria-label={t('waypoint.matches.label', { field: c.title, amount: money(want) })} onClick={() => match(c.field)}
          >
            <span className="checkin-match__tick" aria-hidden="true"><Icon name="check" size={14} strokeWidth={3} /></span>
            {t('waypoint.matches')}
          </button>
        </div>
        <p className="checkin-card__meta" id={`${inputId}-expected`}>
          <span>{t('waypoint.expected', { amount: money(want) })}</span>
          {isTouched && <span className="checkin-card__state--typed">{isMatched ? t('waypoint.matches') : t('waypoint.typed')}</span>}
        </p>
        <p id={`${inputId}-error`} className="checkin__refusal" data-testid={error ? 'check-in-field-refused' : undefined}>{error}</p>
        {suggestion && (
          <p className="checkin-card__suggest" data-testid="check-in-suggestion">
            <Icon name="plusCircle" size={15} />
            <span>
              {t(suggestion === 'cost' ? 'waypoint.suggest.cost' : 'waypoint.suggest.income')}
              <Link to={`/plan?add=${suggestion === 'cost' ? 'expense' : 'income'}&amount=${perMonth}`} className="text-link" onClick={onClose}>{t('waypoint.suggest.add')}</Link>
            </span>
          </p>
        )}
      </div>
    );
  }
}

const causeText = (c: CheckInCause): string => {
  switch (c.kind) {
    case 'added': return t('waypoint.done.cause.added', { label: c.label });
    case 'changed': {
      const gap = Number(c.actual ?? '0') - Number(c.expected ?? '0');
      return t(gap > 0 ? 'waypoint.done.cause.more' : 'waypoint.done.cause.less', { label: c.label, amount: money(Math.abs(gap)) });
    }
    case 'skipped': return t('waypoint.done.cause.skipped', { label: c.label, date: dayMonth(parseDay(c.date!)) });
    case 'markets': return t('waypoint.done.cause.markets');
    case 'debt': return t('waypoint.done.cause.debt', { label: c.label });
    case 'not-on-map':
      if (c.starting_estimate) return t('waypoint.done.cause.startingEstimate');
      return t(Number(c.effect) < 0 ? 'waypoint.done.cause.notOnMap.out' : 'waypoint.done.cause.notOnMap.in');
    case 'balance': return c.label;
  }
};

function DoneStep({ headingId, result, date, changes, estimateNote, onClose }: {
  headingId: string; result: CheckInResult | null; date: string; changes: readonly MapChange[]; estimateNote: string | null; onClose: () => void;
}) {
  const day = parseDay(date);
  const next = core.addMonths(day, 1);
  const difference = result ? Number(result.difference) : 0;
  const lead = Math.round(difference) === 0
    ? t('waypoint.done.same')
    : t(difference < 0 ? 'waypoint.done.lower' : 'waypoint.done.higher', { amount: money(Math.abs(difference)) });
  const causes = result?.reviewed
    ? [...result.causes].filter((c) => Math.round(Number(c.effect)) !== 0).sort((a, b) => Math.abs(Number(b.effect)) - Math.abs(Number(a.effect)))
    : [];
  return (
    <div className="checkin-done">
      <span className="checkin-done__tick" aria-hidden="true"><Icon name="check" size={28} strokeWidth={3} /></span>
      <h2 id={headingId} className="checkin__title checkin-done__title" tabIndex={-1}>{t('waypoint.done.title')}</h2>
      {result && (
        <>
          <p className="checkin-done__net">{t('waypoint.done.netWorth', { amount: money(Number(result.net_worth)), date: dayMonth(day) })}</p>
          <p className={`checkin-done__lead ${Math.round(difference) === 0 ? '' : difference < 0 ? 'checkin-done__lead--lower' : 'checkin-done__lead--higher'}`} data-testid="check-in-gap">{lead}</p>
          <dl className="checkin-done__compare">
            <div><dt>{t('waypoint.done.expected')}</dt><dd>{money(Number(result.expected.expected.net_worth))}</dd></div>
            <div><dt>{t('waypoint.done.actual')}</dt><dd>{money(Number(result.net_worth))}</dd></div>
          </dl>
          <section className="checkin-done__section" aria-labelledby={`${headingId}-why`}>
            <h3 className="checkin__group-title" id={`${headingId}-why`}>{t('waypoint.done.why')}</h3>
            {result.reviewed ? (
              <ul className="checkin-done__list" data-testid="check-in-why">
                {causes.length === 0 && <li className="checkin-done__row">{t('waypoint.done.cause.none')}</li>}
                {causes.map((c) => (
                  <li key={`${c.kind}|${c.item ?? ''}|${c.date ?? ''}|${c.label}`} className="checkin-done__row" data-testid="check-in-cause">
                    <span>{causeText(c)}</span>
                    <span className={`checkin-done__value ${Number(c.effect) < 0 ? 'checkin-done__value--lower' : 'checkin-done__value--higher'}`}>{money(Number(c.effect), { sign: true })}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <>
                <ul className="checkin-done__list" data-testid="check-in-why">
                  {result.balances.map((b) => <BalanceLine key={b.field} change={b} />)}
                </ul>
                {result.causes.length > 0 && <p className="checkin-done__note">{t('waypoint.done.byBalance')}</p>}
              </>
            )}
          </section>
          {result.impact.goals.length > 0 && (
            <section className="checkin-done__section" aria-labelledby={`${headingId}-goals`}>
              <h3 className="checkin__group-title" id={`${headingId}-goals`}>{t('waypoint.done.goals')}</h3>
              <ul className="checkin-done__list" data-testid="check-in-goals">
                {[...result.impact.goals].sort((a, b) => Number(b.months_sooner !== 0) - Number(a.months_sooner !== 0)).slice(0, 4).map((g) => <GoalLine key={g.id} goal={g} />)}
              </ul>
            </section>
          )}
          <BiggestMove headingId={headingId} onClose={onClose} />
        </>
      )}
      {changes.length > 0 && (
        <p className="checkin-done__note checkin-done__note--map" data-testid="check-in-map-changes">
          {t('waypoint.done.mapChanged', {
            changes: changes.map((c) => t(c.kind === 'added' ? 'waypoint.done.mapChange.monthly' : 'waypoint.done.mapChange', { label: c.label, amount: money(Number(c.amount)), date: dayMonth(parseDay(c.from)) })).join(', '),
          })}
        </p>
      )}
      <p className="checkin-done__note" data-testid="check-in-estimate">{estimateNote ?? t('waypoint.done.allChecked')}</p>
      <p className="checkin-done__next">{t('waypoint.done.next', { date: dayMonth(next) })}</p>
    </div>
  );
}

const moveAmount = (amount: string) => {
  const n = Number(amount);
  return money(n, { pence: n < 100 && !Number.isInteger(n) });
};

/** A move's cost in plain words, from views' fields: "£21 a month less on Eating out, into Stocks & Shares ISA". */
function moveCost(m: Move): string {
  const words = { amount: moveAmount(m.amount), per: t(`move.per.${m.frequency}` as MessageKey), item: m.item.label, to: m.to?.label ?? '' };
  const cost = m.kind === 'cut'
    ? t(m.to && m.to.kind !== 'cash' ? 'move.cost.cut' : 'move.cost.cut.cash', words)
    : m.kind === 'redirect' ? t('move.cost.redirect', { ...words, month: monthYear(parseDay(m.from)) })
      : m.kind === 'pension' ? t('move.cost.pension', { ...words, percent: m.percent_of_pay ?? 0 })
        : t('move.cost.overpay', words);
  return m.then ? t('move.cost.then', { cost, then: m.then.label }) : cost;
}

/** What it does for the goal: "1 month sooner, Sep 2028", "About 3 weeks sooner", "£534 closer by Oct 2028". */
function moveEffect(m: Move): string {
  const e = m.effect;
  const month = (iso: string) => monthYear(parseDay(iso));
  if (e.becomes_reachable && e.after !== null) return t('move.effect.reachable', { month: month(e.after) });
  const behind = e.shortfall_before !== undefined && Number(e.shortfall_before) > 0;
  const onTrack = e.shortfall_before !== undefined && !behind;
  const closer = t(onTrack ? 'move.effect.more' : 'move.effect.closer', { amount: money(Number(e.gain)), month: month(e.measured_on) });
  if (behind && Number(e.shortfall_after) === 0 && e.after !== null) return t('move.effect.onTrack', { month: month(e.after) });
  if (behind || e.after === null) return closer;
  if (e.months_sooner) return e.months_sooner === 1 ? t('move.effect.sooner.one', { month: month(e.after) }) : t('move.effect.sooner', { n: e.months_sooner, month: month(e.after) });
  const weeks = Math.round((e.days_sooner ?? 0) / 7);
  return weeks >= 2 ? t('move.effect.weeks', { span: daysSpan(weeks * 7) }) : closer;
}

/** The moves Done can offer: never the date move, and only ones whose change set fits a link. */
const offered = (moves: readonly Move[]) => moves.filter((m) => m.kind !== 'date' && encodeChanges(m.changes).length <= MAX_CHANGES_PARAM);

/**
 * "Your biggest move": the engine's top lever for the first goal still to reach, on the map as just
 * saved, one tap from What If. Worked out after Done has painted (it runs dozens of forecasts); nothing
 * shows when there is no move worth making.
 */
function BiggestMove({ headingId, onClose }: { headingId: string; onClose: () => void }) {
  const { plan, waypoints } = useStore();
  const [found, setFound] = useState<{ move: Move; goal: string } | null>(null);
  useEffect(() => {
    if (!plan) return;
    const timer = setTimeout(() => {
      const result = biggestMoves(plan, waypoints, todayString());
      const move = offered(result.moves)[0];
      const goal = (plan.goals ?? []).find((g) => g.id === result.goalId);
      setFound(move && goal ? { move, goal: goal.label ?? '' } : null);
    }, 0);
    return () => clearTimeout(timer);
  }, [plan, waypoints]);
  if (!found) return null;
  const { move, goal } = found;
  const cost = moveCost(move);
  return (
    <section className="checkin-done__section" aria-labelledby={`${headingId}-move`} data-testid="check-in-move" data-move={move.id}>
      <h3 className="checkin__group-title" id={`${headingId}-move`}>{t('waypoint.done.move.title')}</h3>
      <div className="checkin-move">
        <span className="checkin-move__icon" aria-hidden="true"><Icon name="trendUp" size={16} /></span>
        <div className="checkin-move__text">
          <p className="checkin-move__cost" data-testid="check-in-move-cost">{cost}</p>
          <p className="checkin-move__effect">
            {goal && <span className="checkin-move__goal">{goal}</span>}
            <span className="checkin-move__gain" data-testid="check-in-move-effect">{moveEffect(move)}</span>
          </p>
        </div>
        <Link
          to={`/what-if?${new URLSearchParams({ from: 'move', c: encodeChanges(move.changes) })}`} className="button button--outline-mint checkin-move__try"
          aria-label={t('waypoint.done.move.tryLabel', { cost })} onClick={onClose} data-testid="check-in-move-try"
        >
          {t('waypoint.done.move.try')}<Icon name="arrowRight" size={16} />
        </Link>
      </div>
    </section>
  );
}

function BalanceLine({ change }: { change: CheckInBalanceChange }) {
  const effect = Number(change.effect);
  const gap = Number(change.actual) - Number(change.expected);
  const text = Math.round(gap) === 0
    ? t('waypoint.done.balance.same', { label: change.label })
    : t(gap < 0 ? 'waypoint.done.balance.lower' : 'waypoint.done.balance.higher', { label: change.label, amount: money(Math.abs(gap)) });
  return (
    <li className="checkin-done__row">
      <span>{text}</span>
      <span className={`checkin-done__value ${Math.round(effect) === 0 ? '' : effect < 0 ? 'checkin-done__value--lower' : 'checkin-done__value--higher'}`}>
        {Math.round(effect) === 0 ? '' : money(effect, { sign: true })}
      </span>
    </li>
  );
}

function GoalLine({ goal }: { goal: CheckInResult['impact']['goals'][number] }) {
  const month = (iso: string | null, withYear: boolean) => (iso === null ? '—' : monthName(parseDay(iso), withYear));
  const sameYear = goal.before !== null && goal.after !== null && goal.before.slice(0, 4) === goal.after.slice(0, 4);
  const moved = goal.months_sooner ?? 0;
  const detail = goal.becomes_reachable ? t('waypoint.done.goal.reachable')
    : goal.becomes_unreachable ? t('waypoint.done.goal.unreachable')
      : moved === 0 ? t('waypoint.done.goal.still', { date: month(goal.after, true) })
        : t(moved > 0 ? 'waypoint.done.goal.earlier' : 'waypoint.done.goal.later', { span: durationText(Math.abs(moved)) });
  const tone = goal.becomes_reachable || moved > 0 ? 'higher' : goal.becomes_unreachable || moved < 0 ? 'lower' : null;
  return (
    <li className="checkin-done__row checkin-done__row--goal">
      <span className="checkin-done__goal">
        <span className="checkin-done__goal-label">{goal.label}</span>
        {moved !== 0 && goal.before !== null && goal.after !== null && (
          <span className="checkin-done__goal-dates">{month(goal.before, !sameYear)} → {month(goal.after, true)}</span>
        )}
      </span>
      <span className={`checkin-done__value ${tone ? `checkin-done__value--${tone}` : ''}`}>{detail}</span>
    </li>
  );
}
